/**
 * Доступ к Telegram WebApp.
 *
 * Всё через один модуль: вне Telegram объекта нет, и половина экранов иначе
 * падала бы при открытии в обычном браузере. Здесь он один раз проверяется,
 * дальше приложение работает с безопасными значениями.
 */

interface WebApp {
  initData: string;
  initDataUnsafe?: {
    user?: {
      id: number;
      language_code?: string;
      /** Разрешено ли боту писать в личку. Telegram ставит это сам. */
      allows_write_to_pm?: boolean;
    };
    /** Хвост ссылки вида t.me/bot/app?startapp=<...> — им приходит приглашение. */
    start_param?: string;
  };
  colorScheme?: 'light' | 'dark';
  themeParams?: Record<string, string>;
  MainButton: {
    setText(text: string): void;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    enable(): void;
    disable(): void;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  ready(): void;
  expand(): void;
  onEvent?(event: string, cb: () => void): void;
  offEvent?(event: string, cb: () => void): void;
  openLink?(url: string, options?: { try_instant_view?: boolean }): void;
  /** Спросить разрешение писать в личку. Bot API 6.9; у старых клиентов нет. */
  requestWriteAccess?(cb?: (granted: boolean) => void): void;
  HapticFeedback?: { impactOccurred(style: string): void };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp };
  }
}

export const webApp: WebApp | undefined = window.Telegram?.WebApp;

/** Открыто ли приложение внутри Telegram. Вне его часть возможностей просто нет. */
export const inTelegram = webApp !== undefined && webApp.initData.length > 0;

export function initTelegram(): void {
  webApp?.ready();
  webApp?.expand();
}

/** Подписанная строка для запросов, требующих подтверждения личности. */
export function getInitData(): string {
  return webApp?.initData ?? '';
}

/** Может ли бот писать этому человеку прямо сейчас. */
export function canBotWrite(): boolean {
  return webApp?.initDataUnsafe?.user?.allows_write_to_pm === true;
}

/**
 * Разрешение боту писать в личку.
 *
 * Нужно потому, что приглашение ведёт сразу в приложение, а не в переписку
 * с ботом: человек может пользоваться всем, ни разу не нажав «Старт», — и
 * тогда первое же письмо о его собственной сделке упрётся в 403, после
 * чего мы его заглушим. Спрашиваем ровно в тот момент, когда он
 * подписывается на уведомления, а не на входе: на входе это выглядит как
 * попрошайничество, а здесь — как часть того, о чём он попросил.
 *
 * Отказ не ломает подписку: кошелёк всё равно подтверждается, а письма
 * начнут доходить, как только человек разрешит или сам напишет боту. У
 * старых клиентов метода нет — там просто идём дальше.
 */
export async function ensureWriteAccess(): Promise<boolean> {
  if (canBotWrite()) return true;
  const ask = webApp?.requestWriteAccess;
  if (ask === undefined) return false;
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const done = (granted: boolean) => {
      if (settled) return;
      settled = true;
      resolve(granted);
    };
    try {
      ask.call(webApp, done);
    } catch {
      done(false);
    }
    // Ответа может не быть вовсе: окно закрыли свайпом, клиент старый,
    // Telegram не прислал событие. Ждать вечно нельзя — за этим ожиданием
    // стоит подтверждение кошелька.
    setTimeout(() => done(false), 20_000);
  });
}

export function getLanguageCode(): string | undefined {
  return webApp?.initDataUnsafe?.user?.language_code;
}

/**
 * Код приглашения из ссылки, по которой открыли приложение.
 *
 * Запоминается сразу и надолго: между переходом по ссылке и покупкой человек
 * успеет и осмотреться, и закрыть приложение, а приглашение к этому моменту
 * уже нигде не лежит — Telegram передаёт его только при открытии.
 *
 * Хранится локально, потому что до подключения кошелька приглашение не к
 * кому привязывать: пара «приглашающий — покупатель» появляется только тогда,
 * когда покупатель известен.
 */
const REF_KEY = '78:ref';

export function captureReferralCode(): void {
  const param = webApp?.initDataUnsafe?.start_param ?? '';
  const match = /^ref[_-]([a-z0-9]{4,16})$/i.exec(param);
  if (match === null) return;
  try {
    // Первая ссылка сильнее: перезаписать её значило бы отобрать доход у
    // того, кто человека действительно привёл.
    if (localStorage.getItem(REF_KEY) === null) {
      localStorage.setItem(REF_KEY, match[1]!.toLowerCase());
    }
  } catch {
    // Хранилище может быть закрыто настройками — тогда приглашение
    // сработает только в этой сессии, и это лучше, чем упасть.
  }
}

export function getReferralCode(): string {
  try {
    return localStorage.getItem(REF_KEY) ?? '';
  } catch {
    return '';
  }
}

export function getColorScheme(): 'light' | 'dark' {
  return webApp?.colorScheme ?? 'dark';
}

/**
 * Внешняя ссылка.
 *
 * В Telegram — через его же метод: обычный переход внутри Mini App уводит
 * человека из приложения без возврата. Вне Telegram — обычное новое окно.
 */
export function openExternal(url: string): void {
  if (webApp?.openLink !== undefined) {
    webApp.openLink(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Подписка на смену темы в Telegram.
 *
 * Тему человек меняет не только до открытия приложения: переключил системную
 * — и Mini App обязан переключиться следом, иначе окажется единственным
 * тёмным окном в светлом телефоне.
 */
export function onThemeChange(cb: () => void): () => void {
  webApp?.onEvent?.('themeChanged', cb);
  return () => webApp?.offEvent?.('themeChanged', cb);
}

export function haptic(): void {
  webApp?.HapticFeedback?.impactOccurred('light');
}
